import React from 'react'
import { CircularProgress } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { IProduct } from '../../../types/products'
import { ILocation } from '../../../types/locations'
import { imageUrl } from '../../../utils/imageUrl'
import { handleImageFallback } from '../../../utils/brand'
import { useAppType } from '../../../context/AppContext'
import { productName } from '../../../components/menu-item/MenuItem'

interface OrderSummaryProps {
  cart: IProduct[]
  takeaway: boolean
  location: ILocation | null
  loading: boolean
  handleCheckout: () => void
  t: (key: string) => string
  showButton?: boolean
}

export const orderTotals = (
  cart: IProduct[],
  takeaway: boolean,
  location: ILocation | null,
) => {
  const totalPrice = cart.reduce(
    (total, item) => total + Number(item.price) * item.quantity!,
    0,
  )
  const deliveryCost = takeaway ? 0 : location?.delivery_price || 0

  return { totalPrice, deliveryCost, grandTotal: totalPrice + deliveryCost }
}

export const OrderButton = ({
  loading,
  disabled,
  onClick,
  label,
}: {
  loading: boolean
  disabled: boolean
  onClick: () => void
  label: string
}) => (
  <button
    className={`checkout-page__order${loading ? ' is-loading' : ''}`}
    disabled={loading || disabled}
    onClick={onClick}
  >
    {loading ? (
      <CircularProgress size={22} thickness={6} sx={{ color: '#fff' }} />
    ) : (
      <>
        {label} <FontAwesomeIcon icon={faArrowRight} />
      </>
    )}
  </button>
)

const OrderSummary: React.FC<OrderSummaryProps> = ({
  cart,
  takeaway,
  location,
  loading,
  handleCheckout,
  t,
  showButton = true,
}) => {
  const appType = useAppType()
  const { totalPrice, deliveryCost, grandTotal } = orderTotals(
    cart,
    takeaway,
    location,
  )

  return (
    <div className='checkout-page__summary'>
      <h3>{t('SUMMARY')}</h3>
      <ul className='checkout-page__summary-items'>
        {cart.map((item, index) => (
          <li key={`${item._id}-${index}`}>
            <img
              src={imageUrl(item.image, 128)}
              alt=''
              loading='lazy'
              onError={handleImageFallback(appType)}
            />
            <span className='checkout-page__summary-name'>{productName(item)}</span>
            <span className='checkout-page__summary-qty'>×{item.quantity}</span>
            <span className='checkout-page__summary-price'>
              {Number(item.price) * item.quantity!} lei
            </span>
          </li>
        ))}
      </ul>
      <div className='checkout-page__summary-rows'>
        <div>
          <span>{t('ORDER_SUM')}</span>
          <span>{totalPrice} lei</span>
        </div>
        <div>
          <span>{t('DELIVERY')}</span>
          <span key={deliveryCost} className='checkout-page__pop'>
            {deliveryCost} lei
          </span>
        </div>
        <div className='is-total'>
          <span>{t('TOTAL')}</span>
          <span key={grandTotal} className='checkout-page__pop'>
            {grandTotal} lei
          </span>
        </div>
      </div>
      {showButton && (
        <OrderButton
          loading={loading}
          disabled={cart.length === 0}
          onClick={handleCheckout}
          label={t('ORDER')}
        />
      )}
    </div>
  )
}

export default OrderSummary
